import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FaLeaf, FaHeart, FaUsers } from 'react-icons/fa';

const AboutContainer = styled.div`
  padding: ${theme.spacing.section} 0;
  background: ${theme.colors.dark};
`;

const AboutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const AboutHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.accent};
  margin-bottom: 64px;
  font-family: ${theme.fonts.heading};
  position: relative;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 48px;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 1.75rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const AboutContent = styled.div`
  display: flex;
  margin-bottom: 50px;
  flex-direction: row;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const AboutText = styled.div`
  flex: 1;
  padding: 0 30px;
  
  @media screen and (max-width: 768px) {
    padding: 0;
    margin-bottom: 30px;
  }
`;

const AboutParagraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${theme.colors.textLight};
  margin-bottom: 20px;
`;

const FeaturesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 20px;
  width: 100%;
  margin-top: 30px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${theme.colors.primaryDark};
  padding: 30px;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.accent};
  margin-bottom: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  color: ${theme.colors.accent};
  margin-bottom: 15px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  color: ${theme.colors.textLight};
  line-height: 1.6;
`;

const AboutSection = () => {
  return (
    <AboutContainer id="about">
      <AboutWrapper>
        <AboutHeading>About Our Approach</AboutHeading>
        <AboutContent>
          <AboutText>
            <AboutParagraph>
              We offer nature-based psychotherapy, coaching and movement coaching for individuals, groups, 
              schools, and organisations. Whether you're looking for 1:1 therapy, a wellbeing day, 
              long-term group interventions, or bespoke programmes for your team or students, our approach 
              integrates the power of nature to heal, connect, and rebuild.
            </AboutParagraph>
            <AboutParagraph>
              In our work together, we'll explore emotional intelligence, self-esteem, and authentic community - 
              through movement, therapeutic work, and deep connection with the outdoors. 
              From schools looking to support students' wellbeing, to organisations building stronger, 
              more resilient teams, we tailor each programme to meet your unique needs.
            </AboutParagraph>
            <AboutParagraph>
              <strong>Rewild your mind. Rebuild your body. Remember your self.</strong>
            </AboutParagraph>
            <AboutParagraph>
              <em>Come as you are. Take what you need.</em>
            </AboutParagraph>
          </AboutText>
        </AboutContent>
        <FeaturesContainer>
          <FeatureCard>
            <FeatureIcon>
              <FaLeaf />
            </FeatureIcon>
            <FeatureTitle>Nature Connection</FeatureTitle>
            <FeatureText>
              Harnessing the healing power of nature through wilderness therapy and outdoor experiences.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>
              <FaHeart />
            </FeatureIcon>
            <FeatureTitle>Holistic Wellbeing</FeatureTitle>
            <FeatureText>
              Integrating mind, body, and spirit through therapeutic practices and movement coaching.
            </FeatureText>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>
              <FaUsers />
            </FeatureIcon>
            <FeatureTitle>Community Building</FeatureTitle>
            <FeatureText>
              Creating authentic connections and supportive communities for lasting change.
            </FeatureText>
          </FeatureCard>
        </FeaturesContainer>
      </AboutWrapper>
    </AboutContainer>
  );
};

export default AboutSection;